import { apiClient } from './api';
import type { Order, CreateOrderDTO, Review } from '@/types';

/**
 * Backend Order Response Format
 * 
 * Backend returns Order entity directly, which has different field names
 * than frontend Order type. This interface matches backend response.
 */
interface BackendOrderResponse {
  id: string;
  orderNumber: string;
  userId: string;
  restaurantId: string;
  status: string;
  deliveryLatitude: number;
  deliveryLongitude: number;
  deliveryAddressText: string;
  orderItems: BackendOrderItem[];
  subtotal: number;
  deliveryFee: number;
  serviceFee: number;
  totalAmount: number;
  paymentMethod?: 'CREDIT_CARD' | 'CASH';
  paymentStatus?: 'PENDING' | 'PAID' | 'FAILED' | 'REFUNDED';
  createdAt: string;
  updatedAt: string;
  estimatedDeliveryTime?: string;
  courierId?: string;
  cancelledAt?: string;
  cancellationReason?: string;
  refundAmount?: number | null;
  refundedAt?: string | null;
  restaurant?: {
    id: string;
    name: string;
    logoUrl?: string | null;
  } | null;
  customerNotes?: string | null;
  isReviewed?: boolean;
  reviewRating?: number | null;
  review?: {
    id: string;
    userId: string;
    restaurantId: string;
    orderId: string;
    rating: number;
    comment: string | null;
    createdAt: string;
  } | null;
}

interface BackendOrderItem {
  id: string;
  productId: string | null;
  productName: string;
  productImageUrl: string | null;
  unitPrice: number;
  quantity: number;
  lineTotal: number;
  optionNames: Array<{ name: string; priceModifier: number }> | null;
  removedIngredients?: Array<{ name: string }> | null;
  specialInstructions?: string | null;
}

/**
 * Map backend order response to frontend Order type
 */
export function mapBackendOrderToFrontendOrder(backendOrder: BackendOrderResponse): Order {
  try {
    if (!backendOrder || !backendOrder.id) {
      console.error('Invalid backend order data:', backendOrder);
      throw new Error('Invalid order data received from backend');
    }

    // Parse delivery address from backend format
    // Backend has: deliveryLatitude, deliveryLongitude, deliveryAddressText
    // Frontend needs: deliveryAddress: { label, fullAddress, latitude, longitude, ... }
    
    // Try to extract label from address text (first part before comma)
    const addressText = backendOrder.deliveryAddressText || '';
    const addressParts = addressText.split(',');
    const label = addressParts[0]?.trim() || 'Adres';

    const orderItems = Array.isArray(backendOrder.orderItems) ? backendOrder.orderItems : [];
    const mappedItems = orderItems.map((item) => {
      if (!item) {
        console.warn('Invalid order item in backend response:', item);
        return null;
      }
      try {
        return {
          menuItemId: item.productId || '', // Backend may have null productId if product deleted
          quantity: Number(item.quantity) || 0,
          unitPrice: Number(item.unitPrice) || 0, // Backend sends in TRY, not cents
          selectedOptions: [],
          specialInstructions: item.specialInstructions || undefined,
          menuItemName: item.productName || 'Ürün', // Backend field: productName
          menuItemDescription: undefined, // Backend doesn't store description
          optionNames: Array.isArray(item.optionNames)
            ? item.optionNames.map((opt: any) => String(opt?.name || '')).filter(Boolean)
            : [],
          removedIngredientNames: Array.isArray(item.removedIngredients)
            ? item.removedIngredients.map((opt: any) => String(opt?.name || '')).filter(Boolean)
            : [],
        };
      } catch (itemError) {
        console.error('Error mapping order item:', item, itemError);
        return null;
      }
    }).filter((item): item is NonNullable<typeof item> => item !== null); // Filter out null items

  const mappedOrder: Order = {
    id: backendOrder.id,
    orderNumber: backendOrder.orderNumber || '',
    userId: backendOrder.userId,
    restaurantId: backendOrder.restaurantId,
    status: backendOrder.status as Order['status'],
    deliveryAddress: {
      label,
      fullAddress: addressText,
      latitude: Number(backendOrder.deliveryLatitude) || 0,
      longitude: Number(backendOrder.deliveryLongitude) || 0,
      // Backend doesn't provide these, set to undefined
      building: undefined,
      floor: undefined,
      apartment: undefined,
      instructions: undefined,
    },
    items: mappedItems,
    subtotal: Number(backendOrder.subtotal) || 0, // Backend sends in TRY, not cents
    deliveryFee: Number(backendOrder.deliveryFee) || 0, // Backend sends in TRY, not cents
    serviceFee: Number(backendOrder.serviceFee) || 0, // Backend sends in TRY, not cents
    total: Number(backendOrder.totalAmount) || 0, // Backend sends in TRY, not cents
    paymentMethod: backendOrder.paymentMethod || 'CASH', // Default to CASH if not provided
    paymentStatus: backendOrder.paymentStatus || 'PENDING', // Default to PENDING if not provided
    createdAt: backendOrder.createdAt || new Date().toISOString(),
    updatedAt: backendOrder.updatedAt || new Date().toISOString(),
    estimatedDeliveryTime: backendOrder.estimatedDeliveryTime,
    courierId: backendOrder.courierId,
    courier: (backendOrder as any).courier ?? undefined,
    customerNotes: backendOrder.customerNotes || (backendOrder as any).customer_notes || undefined,
    cancellationReason: backendOrder.cancellationReason || (backendOrder as any).cancellation_reason || null,
    cancelledAt: backendOrder.cancelledAt || (backendOrder as any).cancelled_at || null,
    refundAmount: backendOrder.refundAmount ?? (backendOrder as any).refund_amount ?? null,
    refundedAt: backendOrder.refundedAt || (backendOrder as any).refunded_at || null,
    isReviewed: backendOrder.isReviewed,
    reviewRating: backendOrder.reviewRating ?? (backendOrder.review?.rating ?? null),
    review: (backendOrder.review as Review | undefined) ?? null,
  };

    if ((backendOrder as any).restaurant) {
      (mappedOrder as any).restaurant = (backendOrder as any).restaurant;
    }

    (mappedOrder as any).deliveryLatitude = Number(backendOrder.deliveryLatitude) || 0;
    (mappedOrder as any).deliveryLongitude = Number(backendOrder.deliveryLongitude) || 0;

    return mappedOrder;
  } catch (error: any) {
    console.error('❌ Error mapping backend order to frontend order:', error);
    console.error('Backend order data:', backendOrder);
    throw new Error(`Failed to map order data: ${error.message}`);
  }
}

export interface CreateOrderResult {
  order: Order;
  trackingToken?: string | null;
  trackingUrl?: string | null;
}

export const orderService = {
  /**
   * Create new order
   */
  async createOrder(data: CreateOrderDTO): Promise<CreateOrderResult> {
    const response = await apiClient.post<BackendOrderResponse & {
      trackingToken?: string | null;
      trackingUrl?: string | null;
    }>('/orders', data);

    const payload = response.data.data;
    const { trackingToken, trackingUrl, ...orderPayload } = payload;

    return {
      order: mapBackendOrderToFrontendOrder(orderPayload),
      trackingToken: trackingToken ?? null,
      trackingUrl: trackingUrl ?? null,
    };
  },

  async trackGuestOrder(token: string): Promise<Order> {
    const response = await apiClient.get<BackendOrderResponse>('/orders/public/track', {
      params: { token },
    });
    return mapBackendOrderToFrontendOrder(response.data.data);
  },

  async resendGuestTrackingLink(orderNumber: string, email: string): Promise<string> {
    const response = await apiClient.post<{ message: string }>(
      '/orders/public/resend-tracking-link',
      { orderNumber, email },
    );
    return response.data.data.message;
  },

  async cancelGuestOrder(token: string, reason?: string): Promise<Order> {
    const response = await apiClient.post<BackendOrderResponse>('/orders/public/cancel', {
      token,
      reason,
    });
    return mapBackendOrderToFrontendOrder(response.data.data);
  },

  async getOrder(id: string): Promise<Order> {
    try {
      console.log('🔍 [orderService] Fetching order:', id);
      
      const response = await apiClient.get<BackendOrderResponse>(`/orders/${id}`);
      
      console.log('📦 [orderService] Raw API response:', response);

      if (!response) {
        console.error('❌ [orderService] No response object');
        throw new Error('No response from server');
      }

      if (!response.data) {
        console.error('❌ [orderService] No response.data:', response);
        throw new Error('Invalid response structure: missing data');
      }

      console.log('📦 [orderService] Response.data:', response.data);

      let orderData: BackendOrderResponse;

      if (response.data.success !== undefined) {
        if (!response.data.success) {
          console.error('❌ [orderService] API returned failure:', response.data.error);
          throw new Error(response.data.error?.message || 'Order not found');
        }
        
        if (!response.data.data) {
          console.error('❌ [orderService] API success but no data:', response.data);
          throw new Error('Order data is missing from response');
        }
        
        orderData = response.data.data;
      } else {
        console.warn('⚠️ [orderService] Response not wrapped in success envelope, using direct data');
        orderData = response.data as any;
      }

      console.log('✅ [orderService] Backend Order Response (raw):', orderData);

      const mappedOrder = mapBackendOrderToFrontendOrder(orderData);

      console.log('✅ [orderService] Mapped Order (frontend):', mappedOrder);

      return mappedOrder;
    } catch (error: any) {
      console.error('❌ [orderService] Error in getOrder:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        stack: error.stack,
      });

      if (error.response) {
        const errorMessage = error.response.data?.error?.message || error.message || 'Failed to load order';
        console.error('❌ [orderService] Axios error response:', error.response.data);
        throw new Error(errorMessage);
      }
      throw error;
    }
  },

  /**
   * Get user orders
   */
  async getUserOrders(): Promise<Order[]> {
    const response = await apiClient.get<BackendOrderResponse[]>('/orders');
    return response.data.data.map(mapBackendOrderToFrontendOrder);
  },

  async getMyOrders(params: {
    segment: 'active' | 'past';
    page?: number;
    limit?: number;
  }): Promise<{ orders: Order[]; total: number; page: number; limit: number }> {
    const response = await apiClient.get<{
      orders: BackendOrderResponse[];
      total: number;
      page: number;
      limit: number;
    }>('/orders/my', {
      params: {
        segment: params.segment,
        page: params.page,
        limit: params.limit,
      },
    });

    const payload = response.data.data;

    return {
      orders: (payload.orders || []).map(mapBackendOrderToFrontendOrder),
      total: Number(payload.total || 0),
      page: Number(payload.page || params.page || 1),
      limit: Number(payload.limit || params.limit || 20),
    };
  },

  async cancelOrder(id: string, reason?: string): Promise<Order> {
    const response = await apiClient.post<BackendOrderResponse>(`/orders/${id}/cancel`, { reason: reason || undefined });
    return mapBackendOrderToFrontendOrder(response.data.data);
  },

  async updateOrderStatus(id: string, status: Order['status']): Promise<Order> {
    const response = await apiClient.patch<BackendOrderResponse>(`/orders/${id}/status`, { status });
    return mapBackendOrderToFrontendOrder(response.data.data);
  },

  async reorderOrder(params: { orderId: string; replaceCart?: boolean }): Promise<{
    summary: {
      addedCount: number;
      skippedMissingProducts: number;
      skippedUnavailableProducts: number;
      skippedRequiredOptions: number;
    };
  }> {
    const response = await apiClient.post<{
      cart: unknown;
      summary: {
        addedCount: number;
        skippedMissingProducts: number;
        skippedUnavailableProducts: number;
        skippedRequiredOptions: number;
      };
    }>(`/orders/${params.orderId}/reorder`, {
      replaceCart: Boolean(params.replaceCart),
    });

    return response.data.data;
  },
};
